import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import { Resource, Action } from '../../../Constants/socket';

/**
 * Custom hook to handle channel real-time socket events
 * @param {Array} channels - Current channels list
 * @param {Function} setChannels - Function to update channels
 * @returns {Object} Socket event data (highlighted rows, soft refresh key)
 */
const useChannelSocketEvents = (channels, setChannels) => {
    const [softRefreshKey, setSoftRefreshKey] = useState(0);
    const [highlightedRowIds, setHighlightedRowIds] = useState(() => new Set());
    const highlightTimeoutsRef = useRef(new Map());

    // Memoize channel IDs for socket subscription
    const channelIds = useMemo(() => {
        return channels.map(channel => channel.id).filter(Boolean);
    }, [channels]);

    // Subscribe to channel resource with specific IDs
    useSocketSubscription(Resource.CHANNEL, channelIds.length > 0 ? channelIds : null);

    const highlightRow = useCallback((id) => {
        if (!id) return;

        setHighlightedRowIds(prev => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });

        const existingTimeout = highlightTimeoutsRef.current.get(id);
        if (existingTimeout) clearTimeout(existingTimeout);

        const timeoutId = setTimeout(() => {
            setHighlightedRowIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            highlightTimeoutsRef.current.delete(id);
        }, 2500);

        highlightTimeoutsRef.current.set(id, timeoutId);
    }, []);

    useEffect(() => {
        return () => {
            // Cleanup highlight timers on unmount
            for (const timeoutId of highlightTimeoutsRef.current.values()) {
                clearTimeout(timeoutId);
            }
            highlightTimeoutsRef.current.clear();
        };
    }, []);

    const handleChannelCreated = useCallback((data) => {
        // Avoid skeleton flash: do a silent/soft refresh and briefly highlight the new row
        if (data?.id) highlightRow(data.id);
        setSoftRefreshKey(prev => prev + 1);
    }, [highlightRow]);

    const handleChannelUpdated = useCallback((data) => {
        // Update the specific channel in state without reloading
        setChannels(prevChannels =>
            prevChannels.map(channel =>
                channel.id === data.id ? { ...channel, ...data } : channel
            )
        );
        if (data?.id) highlightRow(data.id);
    }, [setChannels, highlightRow]);

    const handleChannelDeleted = useCallback((data) => {
        // Remove the deleted channel from state
        setChannels(prevChannels =>
            prevChannels.filter(channel => channel.id !== data.id)
        );
    }, [setChannels]);

    // Register socket event listeners
    useSocketEvent(Resource.CHANNEL, Action.CREATE, handleChannelCreated);
    useSocketEvent(Resource.CHANNEL, Action.UPDATE, handleChannelUpdated);
    useSocketEvent(Resource.CHANNEL, Action.DELETE, handleChannelDeleted);

    return {
        highlightedRowIds,
        softRefreshKey,
    };
};

export default useChannelSocketEvents;
